'use client'

import {
  Accordion,
  AccordionChevron,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  List,
  ListItem,
  ListItemAction,
  ListItemContent,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@dafiti/closet-ui'
import { useResponsive } from '@dafiti/closet-ui/hooks'
import { Chevron } from '@dafiti/closet-ui/icons'
import React, { cloneElement, useCallback, useId, useMemo } from 'react'
import type { ComponentProps, ReactElement } from 'react'
import { useAppLayoutContext } from '@/providers/AppLayoutProvider'
import { useIsClient } from '@/hooks/useIsClient'

type Icon = ReactElement<
  { className?: string } & Partial<ComponentProps<'img'>>
>

export type FooterLinkSectionProps = {
  title: string
  icon: Icon
  items: Array<{
    label: string
    href: string
    icon?: Icon
  }>

  /**
   * Number of columns to display the items on large screens
   */
  columns?: number

  /**
   * Enable the router link (next/link) inside the injected LinkComponent.
   * When false, the LinkComponent falls back to a plain <a> tag.
   */
  useRouterLink?: boolean
}

export const FooterLinkSection = (props: FooterLinkSectionProps) => {
  const { userAgent, LinkComponent } = useAppLayoutContext()

  const { isMobile } = useResponsive({ userAgent: userAgent })
  const isClient = useIsClient()

  const headerId = useId()
  const contentId = useId()

  const iconProps: Partial<ComponentProps<'img'>> = useMemo(
    () => ({
      loading: 'lazy',
      fetchPriority: 'low',
      decoding: 'async',
      'aria-hidden': 'true',
    }),
    [],
  )

  const renderSectionHeader = useCallback(
    (as?: React.ElementType) => (
      <ListItem
        suppressHydrationWarning
        id={headerId}
        className="px-3xs md:px-sm w-full !bg-transparent lg:p-0"
        asChild
        as={as}
      >
        <ListItemIcon className="size-4 lg:hidden">
          {cloneElement(props.icon, { className: 'size-4', ...iconProps })}
        </ListItemIcon>
        <ListItemContent>
          <ListItemText className="!text-left font-medium lg:font-bold">
            {props.title}
          </ListItemText>
        </ListItemContent>
        <ListItemAction className="lg:hidden">
          <AccordionChevron className="h-[6px] w-[12px]" aria-hidden="true" />
        </ListItemAction>
      </ListItem>
    ),
    [iconProps, props.icon, props.title, headerId],
  )

  if (!isMobile || !isClient) {
    return (
      <>
        {renderSectionHeader('h3')}

        <ul
          style={{ columnCount: props.columns }}
          className="h-0 list-none overflow-hidden border-b border-b-gray-300 lg:mt-4 lg:h-auto lg:border-none"
        >
          {props.items.map((item) => (
            <li className="mb-3 last:mb-0" key={item.label}>
              <Typography
                as={LinkComponent}
                href={item.href}
                useRouterLink={props.useRouterLink}
                variant="body-3"
                className="flex gap-3 text-gray-500 hover:underline"
              >
                {item.icon &&
                  cloneElement(item.icon, {
                    className: 'size-4',
                    ...iconProps,
                  })}

                {item.label}
              </Typography>
            </li>
          ))}
        </ul>
      </>
    )
  }

  return (
    <AccordionItem
      as="div"
      value={props.title}
      className="data-[state=open]:mb-3xs w-full border-b border-b-gray-300 data-[state=open]:border-b-transparent lg:border-b-transparent"
    >
      <AccordionTrigger className="w-full" asChild>
        {renderSectionHeader()}
      </AccordionTrigger>
      <AccordionContent
        suppressHydrationWarning
        id={contentId}
        as={List}
        className="px-3xs md:px-sm"
      >
        {props.items.map((item) => (
          <ListItem
            key={item.label}
            className="w-full bg-gray-100 py-6"
            as={LinkComponent}
            href={item.href}
            useRouterLink={props.useRouterLink}
          >
            <ListItemContent>
              <Typography variant="body-3">{item.label}</Typography>
            </ListItemContent>

            <ListItemAction>
              <Chevron className="h-[6px] w-[12px]" />
            </ListItemAction>
          </ListItem>
        ))}
      </AccordionContent>
    </AccordionItem>
  )
}

FooterLinkSection.Container = ({
  children,
  defaultValue,
}: {
  children: React.ReactNode
  defaultValue?: Array<string>
}) => {
  return (
    <Accordion
      as="ul"
      className="relative contents w-full list-none"
      type="multiple"
      defaultValue={defaultValue}
      role="list"
    >
      {children}
    </Accordion>
  )
}
